/**
 * 群组匹配服务
 * 负责根据字段值在接收者列表中进行模糊匹配
 */

import type {
  ReceiverInfo,
  MatchedRecord,
  RecordData,
  MatchingResult,
  GroupedRecords,
} from "../types";

/**
 * 执行匹配逻辑
 * @param records 表格记录列表
 * @param receivers 消息接收者列表
 * @param selectedFieldIds 用于匹配的字段 ID 列表
 * @returns 匹配结果
 */
export function matchRecordsToReceivers(
  records: RecordData[],
  receivers: ReceiverInfo[],
  selectedFieldIds: string[],
): MatchingResult {
  const matchedRecordsMap = new Map<number, MatchedRecord[]>();
  const unmatchedRecords: MatchedRecord[] = [];

  // 预处理接收者的 bizKeys，解析 JSON 并提取后缀
  const receiversWithParsedKeys = receivers.map((receiver) => {
    let bizKeyArray: string[] = [];
    let bizKeySuffixes: string[] = [];

    try {
      // 解析 JSON 字符串
      bizKeyArray = JSON.parse(receiver.bizKeys || "[]");

      // 从每个 biz_key 中提取后缀
      // 例如："send_driver_message_G002人广店" -> "G002人广店"
      bizKeySuffixes = bizKeyArray
        .map((bizKey) => {
          // 找到最后一个下划线后的内容
          const parts = bizKey.split("_");
          // 如果有多个部分，取最后一个作为后缀
          const suffix = parts.length > 1 ? parts[parts.length - 1] : bizKey;
          return suffix;
        })
        .filter(Boolean);
    } catch (error) {
      // 解析失败，跳过此接收者
    }

    return {
      ...receiver,
      parsedBizKeys: bizKeyArray,
      bizKeySuffixes,
    };
  });

  // 遍历每条记录
  for (const record of records) {
    let matched = false;
    let matchedReceiver: ReceiverInfo | null = null;
    let matchedSuffix = "";

    // 提取选中字段的值
    const fieldValues = selectedFieldIds
      .map((fieldId) => {
        const value = record.fields[fieldId];
        // 处理不同字段类型
        if (Array.isArray(value)) {
          return value.join("");
        }
        return String(value || "");
      })
      .filter(Boolean)
      .join("");

    if (!fieldValues) {
      // 字段值为空，标记为未匹配
      unmatchedRecords.push({
        ...record,
        isMatched: false,
        matchedValue: "",
      });
      continue;
    }

    // 在接收者列表中查找匹配项
    // 第一轮：精确匹配 - 字段值与 biz_key 后缀完全相等
    for (const receiver of receiversWithParsedKeys) {
      const matchedSuffixIndex = receiver.bizKeySuffixes.findIndex(
        (suffix) => suffix === fieldValues,
      );

      if (matchedSuffixIndex !== -1) {
        matched = true;
        matchedReceiver = receiver;
        matchedSuffix = receiver.bizKeySuffixes[matchedSuffixIndex];
        break;
      }
    }

    // 第二轮：后缀以字段值结尾（仅在精确匹配无结果时）
    // 例如：biz_key 后缀 "G002人广店" 能匹配字段值 "人广店"
    if (!matched) {
      for (const receiver of receiversWithParsedKeys) {
        const matchedSuffixIndex = receiver.bizKeySuffixes.findIndex(
          (suffix) => suffix.endsWith(fieldValues) && suffix !== fieldValues,
        );

        if (matchedSuffixIndex !== -1) {
          matched = true;
          matchedReceiver = receiver;
          matchedSuffix = receiver.bizKeySuffixes[matchedSuffixIndex];
          break;
        }
      }
    }

    if (matched && matchedReceiver) {
      // 匹配成功
      const matchedRecord: MatchedRecord = {
        ...record,
        receiverId: matchedReceiver.id,
        receiverName: matchedReceiver.name,
        groupName: matchedReceiver.groupName,
        matchedValue: fieldValues,
        isMatched: true,
      };

      if (!matchedRecordsMap.has(matchedReceiver.id)) {
        matchedRecordsMap.set(matchedReceiver.id, []);
      }
      matchedRecordsMap.get(matchedReceiver.id)!.push(matchedRecord);
    } else {
      // 匹配失败
      unmatchedRecords.push({
        ...record,
        isMatched: false,
        matchedValue: fieldValues,
      });
    }
  }

  // 构建分组结果
  const groups: GroupedRecords[] = Array.from(matchedRecordsMap.entries()).map(
    ([receiverId, records]) => ({
      receiverId,
      receiverName: records[0].receiverName || "",
      groupName: records[0].groupName || "",
      records,
    }),
  );

  return {
    groups,
    unmatchedRecords,
    totalRecords: records.length,
    matchedCount: records.length - unmatchedRecords.length,
    unmatchedCount: unmatchedRecords.length,
  };
}
